import { useMemo } from 'react'

import { DateTime } from 'luxon'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext, { PageContextPayload } from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import type { CampaignItemModel, OrderModel } from '@models/types'

import BulkReconcileModal from '@modals/BulkReconcileModal'
import EditReconciliationItemModal from '@modals/EditReconciliationItemModal'

import PageContent from './components/PageContent'

const defaultState = {
  showBulkReconcileModal: false,
  showEditReconciliationItemModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkReconcileModal: {
      closeModal: () => setState({ showBulkReconcileModal: false }),
    },
    EditReconciliationItemModal: {
      closeModal: () => setState({ showEditReconciliationItemModal: false }),
      updateReconciliationItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const OrderReconciliation = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showBulkReconcileModal,
    showEditReconciliationItemModal,
  } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = useFilters({
    defaultFilters: {
      approval_date_end: DateTime.now().toFormat('yyyy-MM-dd'),
      approval_date_start: DateTime.now().minus({ month: 1 }).toFormat('yyyy-MM-dd'),
      campaign_id: null,
      provider_id: null,
    },
  })

  const modalContext = useModals({ setState })

  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showBulkReconcileModal: (payload: { item: (CampaignItemModel | OrderModel) }) => {
        setModalData('BulkReconcileModal', payload)
        setState({ showBulkReconcileModal: true })
      },
      showEditReconciliationItemModal: (payload: { item: (CampaignItemModel | OrderModel) }) => {
        setModalData('EditReconciliationItemModal', payload)
        setState({ showEditReconciliationItemModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <BulkReconcileModal
          callbacks={callbacks('BulkReconcileModal', setState)}
          showModal={showBulkReconcileModal}
        />

        <EditReconciliationItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditReconciliationItemModal', setState)}
          showModal={showEditReconciliationItemModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default OrderReconciliation
